import { showConfirmDialog } from '@jarvis/platform-ui';
import type { CompanyHoliday } from '../../../api/companyHolidays';
import { collectHolidaysForDraft } from './timeOffHolidayExclusion';

function formatHolidayList(holidays: { date: string; name: string }[]): string {
  return holidays.map((h) => `${h.name} (${h.date})`).join(', ');
}

/** Warn when creating time off that overlaps company holidays; returns false if the user cancels. */
export async function confirmCompanyHolidayTimeOffCreate(
  startLocal: Date,
  endLocal: Date,
  isAllDay: boolean,
  holidays: CompanyHoliday[]
): Promise<boolean> {
  const overlapping = collectHolidaysForDraft(startLocal, endLocal, isAllDay, holidays);
  if (overlapping.length === 0) return true;

  const holidayList = formatHolidayList(overlapping);
  const splitHint =
    overlapping.length === 1 && expandSingleDayOverlap(startLocal, endLocal, isAllDay, overlapping[0].date)
      ? 'That day will be excluded from your time off.'
      : 'Those days will be excluded and your request may be split into separate entries.';

  return showConfirmDialog({
    title: 'Company holiday overlap',
    message: `This request includes company holidays: ${holidayList}. ${splitHint} You can still create it — double-check the dates are correct.`,
    confirmText: 'Create anyway',
    cancelText: 'Go back'
  });
}

function expandSingleDayOverlap(
  startLocal: Date,
  endLocal: Date,
  isAllDay: boolean,
  holidayDate: string
): boolean {
  const startKey = `${startLocal.getFullYear()}-${String(startLocal.getMonth() + 1).padStart(2, '0')}-${String(startLocal.getDate()).padStart(2, '0')}`;
  if (!isAllDay) return startKey === holidayDate;
  const endExclusive = new Date(endLocal);
  const days: string[] = [];
  for (let cursor = new Date(startLocal); cursor < endExclusive; cursor.setDate(cursor.getDate() + 1)) {
    days.push(
      `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`
    );
  }
  return days.length === 1 && days[0] === holidayDate;
}

export async function confirmCompanyHolidayTimeOffCreateMany(
  drafts: { startLocal: Date; endLocal: Date; isAllDay: boolean }[],
  holidays: CompanyHoliday[]
): Promise<boolean> {
  const seen = new Map<string, string>();
  for (const draft of drafts) {
    for (const h of collectHolidaysForDraft(draft.startLocal, draft.endLocal, draft.isAllDay, holidays)) {
      seen.set(h.date, h.name);
    }
  }
  if (seen.size === 0) return true;

  const holidayList = [...seen.entries()].map(([date, name]) => `${name} (${date})`).join(', ');
  return showConfirmDialog({
    title: 'Company holiday overlap',
    message: `This request includes company holidays: ${holidayList}. Those days will be excluded and your request may be split into separate entries. You can still create it — double-check the dates are correct.`,
    confirmText: 'Create anyway',
    cancelText: 'Go back'
  });
}
